import { ROOT_ID, blendDrawing, canBlend, flattened, outlineIn, subpathsOfCommands } from '@flobi/artwork-core';
import type { IconName } from '../chrome/icons';
import { holding } from '../../ui/hold';
import type { Point, RailButton, Tool, ToolContext, Well } from './types';
import { blendSelection } from '../blendSelection';

/**
 * The Blend tool: the shapes and colours that lie between two objects.
 *
 * Two objects and a blend is not two objects with something drawn between them — it is
 * one live thing whose middle is worked out from its ends. That is what makes it worth
 * being a kind of object rather than a command: move an end, recolour it, drag one of
 * its anchors, and everything between follows. A blend that had baked its steps would be
 * a group with a misleading name and a decision you could not take back.
 *
 * Point at one object, point at another, and the two are joined. Everything else here is
 * feedback, because the whole difficulty of this tool is knowing what you are about to
 * join to what:
 *
 *   — whatever the pointer is over is outlined, so "this one" is never a guess;
 *   — the object already picked stays outlined, more strongly, so you can see you are
 *     half way through;
 *   — a line runs from where you clicked to where the pointer is, which is the blend
 *     you are about to make, drawn before you commit to it;
 *   — Escape puts the first one back.
 */

type Spacing = 'smooth' | 'steps' | 'distance';

const SPACING: { id: Spacing; icon: IconName; title: string }[] = [
  { id: 'smooth', icon: 'smooth', title: 'Smooth colour — as many steps as the colour change needs' },
  { id: 'steps', icon: 'blend-steps', title: 'Steps — a set number between each pair of ends' },
  { id: 'distance', icon: 'spacing', title: 'Distance — a step every so far along, however long the gap' },
];

/** How finely an outline is walked for drawing it on the overlay. */
const SMOOTHNESS = 12;

/** The Blend tool, and what a selected blend can be told to do without the tool in hand. */
export interface BlendTool extends Tool {
  /** Blend all eligible selected endpoints in a single undoable edit. */
  make(ctx: ToolContext): void;
  /** Turns the chosen blends into the shapes they draw. */
  expand(ctx: ToolContext): void;
  /** Takes the steps away and hands back the ends. */
  release(ctx: ToolContext): void;
}

export function blendTool(): BlendTool {
  let spacing: Spacing = 'smooth';
  let steps = 8;
  let distance = 20;
  /** The object picked first, and the place on it that was clicked. */
  let from: { id: string; at: Point } | null = null;
  /** What the pointer is over, with its outline ready to draw. */
  let over: { id: string; rings: Point[][] } | null = null;
  /** The first object's outline, kept so it is not rebuilt every frame. */
  let held: Point[][] = [];
  let at: Point | null = null;

  /** A node's outline in page coordinates, or nothing if it has none to give. */
  const ringsOf = (ctx: ToolContext, id: string): Point[][] => {
    const node = ctx.node(id);
    if (!node) return [];
    const cmds = outlineIn(ctx.scene, node, ctx.scene.worldTransform(id));
    if (!cmds) return [];
    return subpathsOfCommands(cmds)
      .map((subpath) => flattened(subpath, SMOOTHNESS))
      .filter((ring) => ring.length >= 2);
  };

  /** What a blend wears that its expanded group can wear too. */
  const carried = (node: object): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    for (const key of ['visible', 'locked', 'opacity', 'isMask', 'animation', 'timing', 'effects', 'blendMode']) {
      const value = (node as Record<string, unknown>)[key];
      if (value !== undefined) out[key] = value;
    }
    return out;
  };

  /**
   * What under this point could be blended: a drawn shape that is not locked — or a blend,
   * which is never an END but can take another shape in.
   *
   * The hit only ever answers with a whole layer, never something inside a blend, so a
   * press on an existing blend lands on the blend itself. Refusing it outright left no way
   * to add a third shape but dragging it in through Layers, and before that it wrapped the
   * old blend inside a new one (QA 2026-09-24, blend-F3).
   */
  const targetOf = (ctx: ToolContext, world: Point): { hit: string | null; id: string | null } => {
    const hit = ctx.hit(world);
    if (!hit) return { hit: null, id: null };
    const node = ctx.node(hit);
    const ok = !!node && !ctx.scene.lockedHere(hit) && (node.type === 'blend' || canBlend(ctx.scene, hit));
    return { hit, id: ok ? hit : null };
  };

  const aiming = (ctx: ToolContext, world: Point): { id: string; rings: Point[][] } | null => {
    const { id } = targetOf(ctx, world);
    if (!id) return null;
    const rings = ringsOf(ctx, id);
    return rings.length > 0 ? { id, rings } : null;
  };

  /** The artboard something sits on, or null on the bare canvas. */
  const artboardOf = (ctx: ToolContext, id: string): string | null => {
    for (const ancestor of ctx.scene.ancestors(id)) if (ctx.node(ancestor)?.type === 'artboard') return ancestor;
    return null;
  };

  /**
   * Whether two things may be joined without either leaving the artboard it is on.
   *
   * A blend has one parent, so its ends have to share one. Made across two artboards it
   * carried an end onto the other artboard, which clipped it and most of the steps out of
   * sight; made with one end on the bare canvas it pulled the other off its artboard
   * (QA 2026-09-24, blend-F6). Moving something to another artboard is a decision the
   * person makes, not a side effect of blending.
   */
  const together = (ctx: ToolContext, a: string, b: string): boolean => artboardOf(ctx, a) === artboardOf(ctx, b);
  const APART = 'These are on different artboards. Put them on the same one to blend them.';

  /** The blend a node belongs to, if it is part of one. */
  const blendAround = (ctx: ToolContext, id: string): string | null => {
    const parent = ctx.scene.parentOf(id);
    return parent && ctx.node(parent)?.type === 'blend' ? parent : null;
  };

  /**
   * The blends the rail is talking about: each one chosen, or holding what is, once each.
   * Every one of them, not the first found — with several chosen, a change reached only one
   * and nothing said which (QA 2026-09-25, blend-F19).
   */
  const chosenBlends = (ctx: ToolContext): string[] => {
    const out: string[] = [];
    for (const id of ctx.selection) {
      const blend = ctx.node(id)?.type === 'blend' ? id : blendAround(ctx, id);
      if (blend && !out.includes(blend)) out.push(blend);
    }
    return out;
  };

  /** A blend that is locked, or holds a locked end: nothing about it can be changed. */
  const lockedBlend = (ctx: ToolContext, id: string): boolean =>
    ctx.scene.lockedHere(id) || ctx.scene.childrenOf(id).some((child) => ctx.scene.lockedHere(child));
  const LOCKED = 'A locked blend, or one with a locked end, can’t be changed. Unlock it first.';

  /** What the hover outline was built for, so a move over the same thing builds nothing. */
  let overId: string | null = null;

  const forget = () => {
    from = null;
    held = [];
    over = null;
    overId = null;
    at = null;
  };

  /**
   * Joins two objects.
   *
   * An object already in a blend takes the other one INTO that blend rather than starting
   * a second one beside it, because a blend of three shapes is one blend — that is how a
   * person means it, and two overlapping blends sharing an end would draw the middle one
   * twice. The same goes for picking the blend itself, which is what a press on one lands
   * on: the shape joins it as its new top end (blend-F3).
   */
  const join = (ctx: ToolContext, a: string, b: string): void => {
    if (a === b) return;
    // The blend picked itself, or the blend around an end that was picked.
    const hostOf = (id: string) => (ctx.node(id)?.type === 'blend' ? id : blendAround(ctx, id));
    const hostA = hostOf(a);
    const hostB = hostOf(b);
    if (hostA && hostB) {
      // Two blends: one would become an end of the other, and a blend has no paint of its
      // own to blend from (blend-F1). Nothing to do but say so.
      if (hostA !== hostB) ctx.notice('Two blends can’t be joined. Release one, then add its shapes to the other.');
      return;
    }
    const host = hostA ?? hostB;
    if (host) {
      const joining = host === hostA ? b : a;
      if (!together(ctx, joining, host)) {
        ctx.notice(APART);
        return;
      }
      ctx.edit([{ op: 'move', id: joining, parentId: host, position: 'front', keepWorldTransform: true }]);
      ctx.setSelection([host]);
      ctx.stopCapturing();
      return;
    }
    if (!together(ctx, a, b)) {
      ctx.notice(APART);
      return;
    }
    // The lower of the two first, so the blend's children read bottom to top like every
    // other stack in the document.
    const order = ctx.scene.paintOrder();
    const [lower, upper] = order.indexOf(a) < order.indexOf(b) ? [a, b] : [b, a];
    const result = ctx.edit([
      {
        op: 'create',
        ref: 'blend',
        parentId: ctx.scene.parentOf(upper) ?? ROOT_ID,
        // In the upper end's place in the stack, not on top of it: whatever sat above the
        // two stays above the blend (QA 2026-09-25, blend-F8).
        position: { before: upper },
        node: { type: 'blend', name: 'Blend', spacing, steps, distance },
      },
      { op: 'move', id: lower, parentId: '$blend', position: 'front', keepWorldTransform: true },
      { op: 'move', id: upper, parentId: '$blend', position: 'front', keepWorldTransform: true },
    ]);
    if (result.ok && result.created.blend) ctx.setSelection([result.created.blend]);
    ctx.stopCapturing();
  };

  /**
   * Turns the chosen blends into the shapes they were drawing, as one undo step.
   *
   * Refused whole when any of them is locked or holds a locked end: the delete at the end
   * took a locked end down with an unlocked blend, and a locked blend did nothing without
   * a word (QA 2026-09-25, blend-F14).
   */
  const expandAll = (ctx: ToolContext, ids: readonly string[]): void => {
    if (ids.some((id) => lockedBlend(ctx, id))) {
      ctx.notice(LOCKED);
      return;
    }
    const ops: unknown[] = [];
    const refs: string[] = [];
    ids.forEach((id, which) => {
      const made = expandOps(ctx, id, `x${which}`);
      if (!made) return;
      ops.push(...made);
      refs.push(`x${which}`);
    });
    if (ops.length === 0) return;
    const result = ctx.edit(ops);
    if (result.ok) ctx.setSelection(refs.map((ref) => result.created[ref]).filter((id): id is string => !!id));
    ctx.stopCapturing();
  };

  /** The ops that turn one blend into a group of what it draws, its refs named from `tag`. */
  const expandOps = (ctx: ToolContext, id: string, tag: string): unknown[] | null => {
    const node = ctx.node(id);
    if (!node) return null;
    const drawing = blendDrawing(ctx.scene, id);
    if (drawing.length === 0) return null;
    const ops: unknown[] = [
      {
        op: 'create',
        ref: tag,
        parentId: ctx.scene.parentOf(id) ?? ROOT_ID,
        // In the blend's place, wearing what the blend wore — its effects, blend mode,
        // visibility and animation used to be dropped (QA 2026-09-24, blend-F2).
        position: { before: id },
        node: { type: 'group', ...carried(node), name: node.name || 'Blend', transform: [...node.transform] },
      },
    ];
    // In the order they were drawn, so the stack that comes out is the stack that was on
    // screen. The two ends ARE the originals: they move into the group as they are, with
    // everything they wear. Only the steps between them are made. Expand used to rebuild
    // the ends from their outline and delete the originals, losing anything a path could
    // not be told about.
    const ends = new Set(ctx.scene.childrenOf(id));
    const moved = new Set<string>();
    let step = 0;
    drawing.forEach((shape, index) => {
      if (shape.sourceId) {
        if (!moved.has(shape.sourceId)) {
          moved.add(shape.sourceId);
          ops.push({ op: 'move', id: shape.sourceId, parentId: `$${tag}`, position: 'front', keepWorldTransform: true });
        }
        return;
      }
      ops.push({
        op: 'create',
        ref: `${tag}step${index}`,
        parentId: `$${tag}`,
        node: {
          type: 'path',
          name: `Step ${++step}`,
          opacity: shape.opacity,
          path: { subpaths: shape.subpaths },
          fills: shape.fills,
          strokes: shape.strokes,
          ...(shape.strokeStyle ? { strokeStyle: shape.strokeStyle } : {}),
        },
      });
    });
    // Ends the drawing leaves out — a hidden one — come along too, still hidden, rather than
    // going down with the blend.
    for (const end of ends) if (!moved.has(end)) ops.push({ op: 'move', id: end, parentId: `$${tag}`, position: 'front', keepWorldTransform: true });
    ops.push({ op: 'delete', id });
    return ops;
  };

  /**
   * Takes the steps away and hands back the objects that were being blended — every chosen
   * blend at once (blend-F19), each end back where the blend sat in the stack rather than
   * on top of everything (blend-F8). The selection moves only if the edit went through: a
   * locked blend used to refuse and still move the selection (QA 2026-09-25, blend-F14).
   */
  const releaseAll = (ctx: ToolContext, ids: readonly string[]): void => {
    if (ids.some((id) => lockedBlend(ctx, id))) {
      ctx.notice(LOCKED);
      return;
    }
    const ops: unknown[] = [];
    const kids: string[] = [];
    for (const id of ids) {
      const parent = ctx.scene.parentOf(id) ?? ROOT_ID;
      const ends = ctx.scene.childrenOf(id);
      if (ends.length === 0) continue;
      // Bottom first, each just behind the blend, so they come out in the order they had.
      for (const child of ends) ops.push({ op: 'move', id: child, parentId: parent, position: { before: id }, keepWorldTransform: true });
      ops.push({ op: 'delete', id });
      kids.push(...ends);
    }
    if (ops.length === 0) return;
    const result = ctx.edit(ops);
    if (result.ok) ctx.setSelection(kids);
    ctx.stopCapturing();
  };

  /**
   * Writes a setting to every chosen blend, and keeps it for the next one made. One slider
   * drag is one undo step — the hold closes it when the hand lets go — rather than one per
   * tick (QA 2026-09-25, blend-F7). Locked blends are left alone rather than refusing the
   * whole batch.
   */
  const setting = (ctx: ToolContext, props: Record<string, unknown>): void => {
    const ids = chosenBlends(ctx).filter((id) => !lockedBlend(ctx, id));
    if (ids.length > 0) ctx.edit(ids.map((id) => ({ op: 'set', id, props })));
    if (!holding()) ctx.stopCapturing();
    ctx.railChanged();
    ctx.refresh();
  };

  return {
    make: (ctx) => {
      const ids = blendSelection(ctx.scene, ctx.selection);
      if (ids.length < 2) return;
      const upper = ids[ids.length - 1]!;
      ctx.stopCapturing();
      const result = ctx.edit([
        { op: 'create', ref: 'blend', parentId: ctx.scene.parentOf(upper) ?? ROOT_ID, position: { before: upper }, node: { type: 'blend', name: 'Blend', spacing, steps, distance } },
        ...ids.map(id => ({ op: 'move', id, parentId: '$blend', position: 'front', keepWorldTransform: true })),
      ]);
      if (result.ok && result.created.blend) ctx.setSelection([result.created.blend]);
      ctx.stopCapturing();
    },
    expand: (ctx) => expandAll(ctx, chosenBlends(ctx)),
    release: (ctx) => releaseAll(ctx, chosenBlends(ctx)),
    id: 'blend',

    activate(ctx) {
      forget();
      ctx.refresh();
    },

    deactivate(ctx) {
      forget();
      ctx.setCursor(null);
      ctx.refresh();
    },

    onPointerDown(_event, world, ctx) {
      // The first pick went (undone, deleted, a collaborator): nothing is half picked any
      // more, so this press starts afresh rather than joining to nothing (blend-F16).
      if (from && !ctx.node(from.id)) forget();
      const found = aiming(ctx, world);
      if (!found) {
        // A press on nothing is "not that one after all", which is the only thing it
        // could reasonably mean while half way through a blend. A press on something that
        // cannot be blended — text, a combined shape — is not that: the pick stays, and
        // the cursor has already said no (QA 2026-09-25, blend-F12).
        if (ctx.hit(world)) {
          ctx.notice('Blend works with vector shapes and groups of vector shapes. Convert text to outlines and keep images or masks outside the group.');
          return true;
        }
        forget();
        ctx.refresh();
        return true;
      }
      if (!from) {
        from = { id: found.id, at: world };
        held = found.rings;
        ctx.refresh();
        return true;
      }
      const first = from.id;
      forget();
      join(ctx, first, found.id);
      ctx.refresh();
      return true;
    },

    onPointerMove(_event, world, ctx) {
      at = world;
      // Which thing is under the hand is cheap; its outline is not. The outline is built
      // only when the hand arrives over something new — it was rebuilt on every move and
      // thrown away, which dragged over big groups (QA 2026-09-25, blend-F20).
      const { hit, id } = targetOf(ctx, world);
      const changed = id !== overId;
      if (changed) {
        overId = id;
        const rings = id ? ringsOf(ctx, id) : [];
        over = id && rings.length > 0 ? { id, rings } : null;
      }
      const found = over;
      // Said before the press, not only after it: a second pick on another artboard would
      // be refused (blend-F6), so the pointer says no while it is over one — and over
      // anything that cannot be blended at all (blend-F12).
      const blends = (one: string) => ctx.node(one)?.type === 'blend';
      const refused =
        (!!hit && !found) ||
        (!!found && !!from && found.id !== from.id && (!together(ctx, from.id, found.id) || (blends(from.id) && blends(found.id))));
      ctx.setCursor(refused ? 'not-allowed' : found ? 'copy' : from ? 'crosshair' : null);
      // A frame when what is under the hand changes, and while a blend is being aimed —
      // the line has to follow the pointer, so that one really does need every move.
      if (changed || from) ctx.refresh();
    },

    onKeyDown(event, ctx) {
      if (event.key !== 'Escape' || !from) return false;
      event.preventDefault();
      forget();
      ctx.refresh();
      return true;
    },

    rail(ctx) {
      const ids = chosenBlends(ctx);
      const id = ids[0] ?? null;
      const node = id ? ctx.node(id) : null;
      const live = node?.type === 'blend' ? node : null;
      const mode = live?.spacing ?? spacing;
      // A locked blend's settings are shown and cannot be changed (blend-F14).
      const locked = ids.length > 0 && ids.every((one) => lockedBlend(ctx, one));

      const top: Well = {
        kind: 'buttons',
        items: SPACING.map((item) => ({
          id: item.id,
          icon: item.icon,
          title: item.title,
          pressed: mode === item.id,
          disabled: locked,
          run: () => {
            spacing = item.id;
            setting(ctx, { spacing: item.id });
          },
        })),
      };

      // A slider has no disabled state here, so a locked blend's goes: nothing to drag that
      // would do nothing (blend-F14).
      const middle: Well | undefined = locked
        ? undefined
        : mode === 'steps'
          ? {
              kind: 'slider',
              icon: 'blend-steps',
              title: 'Steps — how many shapes go between each pair',
              value: live?.steps ?? steps,
              min: 1,
              max: 64,
              step: 1,
              onChange: (value) => {
                steps = Math.round(value);
                setting(ctx, { steps });
              },
            }
          : mode === 'distance'
            ? {
                kind: 'slider',
                icon: 'spacing',
                title: 'Distance — how far apart the steps sit',
                value: live?.distance ?? distance,
                min: 2,
                max: 200,
                step: 1,
                format: (value) => `${Math.round(value)} px`,
                onChange: (value) => {
                  distance = Math.round(value);
                  setting(ctx, { distance });
                },
              }
            : undefined;

      // Only when there is one to do it to: a button that does nothing is a button that
      // has to be tried before anybody learns it does nothing.
      const bottom: Well | undefined = id
        ? {
            kind: 'buttons',
            items: [
              { id: 'expand', icon: 'expand', title: 'Expand — turn this blend into the shapes it draws', disabled: locked, run: () => expandAll(ctx, chosenBlends(ctx)) },
              { id: 'release', icon: 'release', title: 'Release — take the steps away and keep the two objects', disabled: locked, run: () => releaseAll(ctx, chosenBlends(ctx)) },
            ] as RailButton[],
          }
        : undefined;

      return { top, middle, bottom };
    },

    draw(overlay, ctx) {
      // The picked shape was undone or deleted: its outline goes with it, rather than a
      // ghost that makes the next click silently do nothing (QA 2026-09-25, blend-F16).
      if (from && !ctx.node(from.id)) forget();
      if (over && !ctx.node(over.id)) {
        over = null;
        overId = null;
      }
      const ring = (rings: Point[][], width: number) => {
        for (const loop of rings) overlay.curve([...loop, loop[0]!].map((point) => ({ at: point })), { width });
      };
      // What is already picked, and what the pointer is over. The picked one is drawn
      // heavier: at a glance the difference between "this is chosen" and "this is under
      // your hand" has to be readable without reading anything.
      if (from) ring(held, 2);
      if (over && over.id !== from?.id) ring(over.rings, 1.5);
      // And the blend about to be made, as a line between the two.
      if (from && at) overlay.line(from.at, at, { width: 1, alpha: 0.9 });
      if (from && ctx.selection.length === 0) overlay.handle(from.at, { filled: true });
    },
  };
}
